import React, { useCallback, useRef, useState } from 'react';

// Pinned rows stay opaque; the shadow is added only once content scrolls beneath them.
export const STICKY_SHADOW_BELOW = 'shadow-[0_1px_0_0_#E4E4E7,0_4px_6px_-2px_rgba(16,24,40,0.08)]';
export const STICKY_SHADOW_ABOVE = 'shadow-[0_-1px_0_0_#E4E4E7,0_-4px_6px_-2px_rgba(16,24,40,0.08)]';

// Stacking order inside the scroll container: body cells < pinned rows < header/footer < open menus.
export const STICKY_LAYER = {
  firstColumn: 'z-10',
  pinnedRowCell: 'z-[15]',
  pinnedRowFirstColumn: 'z-[25]',
  header: 'z-30',
  headerFirstColumn: 'z-40',
  footer: 'z-30',
  footerFirstColumn: 'z-40',
  menuOpen: 'z-50',
} as const;

export interface StickyScrollState {
  scrolled: boolean;
  atBottom: boolean;
  stuckGroup: string | null;
}

const INITIAL_SCROLL_STATE: StickyScrollState = { scrolled: false, atBottom: true, stuckGroup: null };

/**
 * Header, top-level group rows and the footer of a table pinned inside its scroll container.
 * Each top-level group renders in its own `<tbody>` registered via `registerGroupBody`; callers
 * re-run `updateScrollState` whenever rows, paging or collapse state change.
 */
export function useStickyTableRows() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const groupBodyRefs = useRef(new Map<string, HTMLTableSectionElement>());
  const observerRef = useRef<ResizeObserver | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [scrollState, setScrollState] = useState<StickyScrollState>(INITIAL_SCROLL_STATE);

  // Header labels can wrap, so pinned rows sit at the header's measured height, not a fixed one.
  const theadRef = useCallback((thead: HTMLTableSectionElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!thead) return;
    const measure = () => setHeaderHeight(thead.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    observerRef.current = new ResizeObserver(measure);
    observerRef.current.observe(thead);
  }, []);

  const registerGroupBody = useCallback(
    (key: string) => (el: HTMLTableSectionElement | null) => {
      if (el) groupBodyRefs.current.set(key, el);
      else groupBodyRefs.current.delete(key);
    },
    []
  );

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const line = el.getBoundingClientRect().top + headerHeight;
    let stuckGroup: string | null = null;
    groupBodyRefs.current.forEach((body, key) => {
      const rect = body.getBoundingClientRect();
      // Stuck while its first row sits on the header and the rest of the group runs beneath it.
      if (rect.top < line - 1 && rect.bottom > line) stuckGroup = key;
    });
    const next: StickyScrollState = {
      scrolled: el.scrollTop > 0,
      atBottom: el.scrollTop + el.clientHeight >= el.scrollHeight - 1,
      stuckGroup,
    };
    setScrollState((prev) =>
      prev.scrolled === next.scrolled && prev.atBottom === next.atBottom && prev.stuckGroup === next.stuckGroup ? prev : next
    );
  }, [headerHeight]);

  const stickyTop: React.CSSProperties = { top: headerHeight };

  return {
    scrollRef,
    theadRef,
    registerGroupBody,
    updateScrollState,
    headerHeight,
    stickyTop,
    scrollState,
    headShadow: scrollState.scrolled ? STICKY_SHADOW_BELOW : '',
    footShadow: scrollState.atBottom ? '' : STICKY_SHADOW_ABOVE,
    groupShadow: (key: string) => (scrollState.stuckGroup === key ? STICKY_SHADOW_BELOW : ''),
  };
}
